import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeader } from '../../../utils/auth';
import { Search, Filter, Eye, Calendar, MapPin, Users } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import JobStatusBadge from '../../../components/production/JobStatusBadge';
import PriorityBadge from '../../../components/production/PriorityBadge';
import '../../../styles/admin/production/JobsList.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const JobsList = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('tous');
  const [filterPriority, setFilterPriority] = useState('tous');
  const [filterCity, setFilterCity] = useState('tous');
  const [filterTeam, setFilterTeam] = useState('tous');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [jobsRes, teamsRes] = await Promise.all([
        axios.get(`${API_URL}/api/jobs?limit=200`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/teams`, { headers: getAuthHeader() })
      ]);

      setJobs(jobsRes.data);
      setTeams(teamsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setLoading(false);
    }
  };

  // Get unique cities for filter
  const cities = [...new Set(jobs.map(j => j.service_address.city))].sort();

  // Filter jobs
  const filteredJobs = jobs.filter(job => {
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch = 
        job.job_number.toLowerCase().includes(search) ||
        job.title.toLowerCase().includes(search) ||
        job.service_address.city.toLowerCase().includes(search) ||
        job.service_address.street.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }

    // Status filter
    if (filterStatus !== 'tous' && job.status !== filterStatus) return false;

    // Priority filter
    if (filterPriority !== 'tous' && job.priority !== filterPriority) return false;

    // City filter
    if (filterCity !== 'tous' && job.service_address.city !== filterCity) return false;

    // Team filter
    if (filterTeam !== 'tous') {
      if (!job.assignment.team_id || job.assignment.team_id !== filterTeam) return false;
    }

    // Date range filter
    if (filterDateFrom || filterDateTo) {
      const jobDate = job.assignment.scheduled_start || job.created_at;
      if (!jobDate) return false;
      
      const jobDateObj = new Date(jobDate);
      if (filterDateFrom && jobDateObj < new Date(filterDateFrom)) return false;
      if (filterDateTo && jobDateObj > new Date(filterDateTo)) return false;
    }

    return true;
  });

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team ? team.name : 'Non assigné';
  };

  if (loading) {
    return <div className="jobs-loading">Chargement...</div>;
  }

  return (
    <div className="jobs-list-container">
      <div className="jobs-list-header">
        <div>
          <h2>Liste des Jobs</h2>
          <p>{filteredJobs.length} job(s) affiché(s)</p>
        </div>
      </div>

      {/* Filters */}
      <div className="jobs-filters">
        <div className="filter-search">
          <Search size={18} />
          <input
            type="text"
            placeholder="Rechercher (numéro, titre, adresse...)" 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-row">
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="tous">Tous les statuts</option>
            <option value="to_schedule">À planifier</option>
            <option value="scheduled">Planifié</option>
            <option value="in_progress">En cours</option>
            <option value="completed">Terminé</option>
            <option value="paused">En pause</option>
            <option value="cancelled">Annulé</option>
          </select>

          <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}>
            <option value="tous">Toutes les priorités</option>
            <option value="low">Basse</option>
            <option value="normal">Normale</option>
            <option value="high">Haute</option>
            <option value="urgent">Urgente</option>
          </select>

          <select value={filterCity} onChange={(e) => setFilterCity(e.target.value)}>
            <option value="tous">Toutes les villes</option>
            {cities.map(city => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>

          <select value={filterTeam} onChange={(e) => setFilterTeam(e.target.value)}>
            <option value="tous">Toutes les équipes</option>
            {teams.map(team => (
              <option key={team.id} value={team.id}>{team.name}</option>
            ))}
          </select>
        </div>

        <div className="filter-date-range">
          <div className="date-input-group">
            <label>Du:</label>
            <input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
            />
          </div>
          <div className="date-input-group">
            <label>Au:</label>
            <input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
            />
          </div>
          {(filterDateFrom || filterDateTo) && (
            <button 
              className="btn-clear-dates"
              onClick={() => {
                setFilterDateFrom('');
                setFilterDateTo('');
              }}
            >
              Effacer dates
            </button>
          )}
        </div>
      </div>

      {/* Jobs Table */}
      <div className="jobs-table-container">
        <table className="jobs-table">
          <thead>
            <tr>
              <th>Numéro</th>
              <th>Titre</th>
              <th>Statut</th>
              <th>Priorité</th>
              <th>Ville</th>
              <th>Équipe</th>
              <th>Date planifiée</th>
              <th>Durée</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredJobs.length === 0 ? (
              <tr>
                <td colSpan="9" className="empty-state">
                  Aucun job trouvé
                </td>
              </tr>
            ) : (
              filteredJobs.map(job => (
                <tr key={job.id}>
                  <td className="job-number-cell">{job.job_number}</td>
                  <td className="job-title-cell">
                    <div className="title-content">
                      <span className="title-main">{job.title}</span>
                      <span className="title-sub">{job.services.length} service(s)</span>
                    </div>
                  </td>
                  <td>
                    <JobStatusBadge status={job.status} />
                  </td>
                  <td>
                    <PriorityBadge priority={job.priority} />
                  </td>
                  <td>
                    <div className="city-cell">
                      <MapPin size={14} />
                      {job.service_address.city}
                    </div>
                  </td>
                  <td>
                    {job.assignment.team_id ? (
                      <div className="team-cell">
                        <Users size={14} />
                        {getTeamName(job.assignment.team_id)}
                      </div>
                    ) : (
                      <span className="text-muted">Non assigné</span>
                    )}
                  </td>
                  <td>
                    {job.assignment.scheduled_start ? (
                      <div className="date-cell">
                        <Calendar size={14} />
                        {format(new Date(job.assignment.scheduled_start), 'dd MMM yyyy HH:mm', { locale: fr })}
                      </div>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>{job.estimated.duration_minutes} min</td>
                  <td>
                    <button
                      onClick={() => navigate(`/admin/production/jobs/${job.id}`)}
                      className="btn-view"
                      title="Voir détails"
                    >
                      <Eye size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default JobsList;